import React, { useState, useRef, useCallback } from 'react';
import styled from 'astroturf/react';
import { Modal } from 'js/design_system/Modal';
import { Icon } from 'js/design_system/Icon';
import { ImpressionWatcher } from 'js/utils/impressions';
import _StyledIcon from "CitationModal-StyledIcon.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/journals/CitationModal.tsx?styleId=StyledIcon";
import _Title from "CitationModal-Title.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/journals/CitationModal.tsx?styleId=Title";
import _CitationStyle from "CitationModal-CitationStyle.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/journals/CitationModal.tsx?styleId=CitationStyle";
import _CitationText from "CitationModal-CitationText.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/journals/CitationModal.tsx?styleId=CitationText";
import _CopiedNotification from "CitationModal-CopiedNotification.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/journals/CitationModal.tsx?styleId=CopiedNotification";


function useCopiedIndex(resetDelay = 2000) {
  const [copiedIndex, setIndex] = useState<number | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const markAsCopied = useCallback(
    (index: number) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      setIndex(index);

      timeoutRef.current = setTimeout(() => {
        setIndex(null);
        timeoutRef.current = null;
      }, resetDelay);
    },
    [resetDelay],
  );

  return [copiedIndex, markAsCopied] as const;
}

const StyledIcon = /*#__PURE__*/styled(Icon, null, {
  displayName: "StyledIcon",
  styles: _StyledIcon
});

type CitationModalProps = {
  open: boolean;
  citations: { name: string; citation: string }[];
  onClose: () => void;
  entity_id: string | number; // used for impression tracking
  source: string; // used for impression tracking
};

const Title = /*#__PURE__*/styled("h1", null, {
  displayName: "Title",
  styles: _Title
});

const CitationStyle = /*#__PURE__*/styled("h3", null, {
  displayName: "CitationStyle",
  styles: _CitationStyle
});

const CitationText = /*#__PURE__*/styled("div", null, {
  displayName: "CitationText",
  styles: _CitationText
});

const CopiedNotification = /*#__PURE__*/styled("div", null, {
  displayName: "CopiedNotification",
  styles: _CopiedNotification
});

type CitationItemProps = {
  name: string;
  citation: string;
  index: number;
  onCopy: (text: string, index: number) => Promise<void>;
  copiedIndex: number | null;
};

function CitationItem({
  name,
  citation,
  index,
  onCopy,
  copiedIndex,
}: CitationItemProps) {
  const isCopied = copiedIndex === index;

  return (
    <div>
      <CitationStyle>
        {name}
        <StyledIcon
          name={isCopied ? 'check' : 'content_copy'}
          size={14}
          onClick={() => onCopy(citation, index)}
        />
      </CitationStyle>
      <CitationText
        onClick={() => onCopy(citation, index)}
        style={{
          position: 'relative',
          backgroundColor: isCopied
            ? 'var(--brand-colors-success-100)'
            : undefined,
        }}
      >
        {citation}
        {isCopied && <CopiedNotification>Copied</CopiedNotification>}
      </CitationText>
    </div>
  );
}

const CitationModal: React.FC<CitationModalProps> = ({
  open,
  citations,
  onClose,
  entity_id,
  source,
}) => {
  const [copiedIndex, markAsCopied] = useCopiedIndex();

  const copyTextToClipboard = async (text: string, index: number) => {
    await navigator.clipboard.writeText(text);
    markAsCopied(index);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ImpressionWatcher source={source} entity_type={15} entity_id={entity_id}>
        <Title>Cite this paper</Title>
        {citations.map(({ name, citation }, index) => (
          <CitationItem
            key={name}
            name={name}
            citation={citation}
            index={index}
            onCopy={copyTextToClipboard}
            copiedIndex={copiedIndex}
          />
        ))}
      </ImpressionWatcher>
    </Modal>
  );
};

export default CitationModal;
